import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import config from '../../config.js';
import '../../styles/global.css';
import '../../styles/requests.css';
import '../../styles/status.css';
import '../../styles/pagination.css';

import PageHeader from '../../components/general/pageHeader.js';
import PageTable from '../../components/general/pageTable';
import EditRequestForm from '../../components/forms/editRequestForm.js';
import CreateRequestForm from '../../components/forms/createRequestForm.js';
import DeleteConfirmation from '../../components/general/deleteConfirmation.js';
import useAuthenticate from '../../components/authentication/authenticate.js';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Loading from '../../components/general/loading.js';

const MRequests = () => {
  const { isLoading } = useAuthenticate();
  const queryClient = useQueryClient();

  const [selectedProperty, setSelectedProperty] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  // Query for properties
  const { data: properties } = useQuery({
    queryKey: ['properties'],
    queryFn: fetchProperties,
  });

  // Query for requests (server-side paginated)
  const { data: requests, isLoading: isRequestsLoading } = useQuery({
    queryKey: ['requests', selectedProperty?.value, currentPage],
    queryFn: () => fetchRequestsByProperty(selectedProperty.value, currentPage, rowsPerPage),
    enabled: !!selectedProperty,
    keepPreviousData: true,
  });

  const [addOverlayOpen, setAddOverlayOpen] = useState(false);
  const [editRequest, setEditRequest] = useState(null);
  const [isEditRequestOpen, setIsEditRequestOpen] = useState(false);
  const [deleteOverlayOpen, setDeleteOverlayOpen] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);

  // Auto-select first property if none selected
  useEffect(() => {
    if (properties?.length > 0 && !selectedProperty) {
      setSelectedProperty(properties[0]);
    }
  }, [properties, selectedProperty]);

  // === Handlers ===
  const handleRequestUpdated = (updatedRequest) => {
    queryClient.invalidateQueries(['requests', selectedProperty?.value]);
    setIsEditRequestOpen(false);
    setEditRequest(null);
  };

  const handleRequestCreated = () => {
    queryClient.invalidateQueries(['requests', selectedProperty?.value]);
    setAddOverlayOpen(false);
  };

  const handleRequestDeleted = async () => {
    if (!requestToDelete) return;

    try {
      await axios.delete(`${config.API_URL}/v2/request/${requestToDelete}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      toast.success('Request deleted successfully!');
      queryClient.invalidateQueries(['requests', selectedProperty?.value]);
    } catch (error) {
      console.error('Error deleting request:', error);
      toast.error('Failed to delete request.');
    }

    setDeleteOverlayOpen(false);
    setRequestToDelete(null);
  };

  const totalPages = requests?.totalPages || 1;
  const maxVisibleButtons = 10;
  const startPage = Math.floor((currentPage - 1) / maxVisibleButtons) * maxVisibleButtons + 1;
  const endPage = Math.min(startPage + maxVisibleButtons - 1, totalPages);

  // === JSX ===
  return (
    <div className="full-page w-full">
      <PageHeader pageName="Service Requests" />

      {isLoading ? (
        <Loading />
      ) : (
        <>
          {/* Property Selector + Create Button */}
          <div className="page-header">
            <Autocomplete
              options={properties}
              value={selectedProperty}
              onChange={(e, newVal) => setSelectedProperty(newVal || null)}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : option?.label || option?.name || ''
              }
              renderInput={(params) => (
                <TextField {...params} label="Select Property" variant="outlined" fullWidth />
              )}
              sx={{ width: '350px' }}
            />
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="header-button" onClick={() => setAddOverlayOpen(true)}>
                <i className="fas fa-receipt"></i>Create Request
              </button>
            </div>
          </div>

          {/* Table */}
          {isRequestsLoading ? (
            <Loading />
          ) : (
            <div className="table-wrapper">
              <PageTable
                rows={requests?.results || []}
                columns={[
                  { label: 'Title', accessor: (row) => row.details?.title || 'N/A', width: '25%' },
                  {
                    label: 'Status',
                    accessor: (row) => {
                      const status = row.status?.toLowerCase();
                      return <span className={`request-status-badge ${status}`}>{row.status}</span>;
                    },
                    width: '5%',
                  },
                  { label: 'Unit', accessor: (row) => row.unit?.unitNumber || 'N/A', width: '5%' },
                  { label: 'Type', accessor: 'type', width: '7%' },
                  { label: 'Priority', accessor: (row) => row.details?.priority || 'N/A', width: '8%' },
                  {
                    label: 'Requested On',
                    accessor: (row) => new Date(row.createdAt).toLocaleDateString(),
                    width: '10%',
                  },
                  {
                    label: 'Requested By',
                    accessor: (row) => {
                      const user = row.requestedBy?.user;
                      if (!user) return 'N/A';
                      return [user.givenName, user.middleName, user.lastName].filter(Boolean).join(' ');
                    },
                    width: '10%',
                  },
                  {
                    label: 'Assignees',
                    accessor: (row) => {
                      if (!row.assignees?.length) return 'N/A';
                      return row.assignees
                        .map((a) => {
                          const user = a.user;
                          if (!user) return null;
                          return [user.givenName, user.middleName, user.lastName]
                            .filter(Boolean)
                            .join(' ');
                        })
                        .filter(Boolean)
                        .join(', ');
                    },
                    width: '15%',
                  },
                  {
                    label: 'Managers',
                    accessor: (row) => {
                      if (!row.managedBy?.length) return 'N/A';
                      return row.managedBy
                        .map((m) => {
                          const user = m.user;
                          if (!user) return null;
                          return [user.givenName, user.middleName, user.lastName]
                            .filter(Boolean)
                            .join(' ');
                        })
                        .filter(Boolean)
                        .join(', ');
                    },
                    width: '15%',
                  },
                ]}
                hasButtons
                hasEdit
                hasDelete
                onEdit={(row) => {
                  console.log('🛠️ Editing row:', row);
                  setEditRequest(row);
                  setIsEditRequestOpen(true);
                }}
                onDelete={(row) => {
                  setRequestToDelete(row._id);
                  setDeleteOverlayOpen(true);
                }}
              />

              {/* Pagination */}
              {requests?.totalPages > 1 && (
                <div className="pagination-controls">
                  <div className="pagination-buttons">
                    <button
                      className="pagination-button"
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>

                    {startPage > 1 && (
                      <button className="pagination-page" onClick={() => setCurrentPage(startPage - 1)} >
                        ...
                      </button>
                    )}

                    {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                      const pageNum = startPage + i;
                      return (
                        <button
                          key={pageNum}
                          className={`pagination-page ${currentPage === pageNum ? 'active' : ''}`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    {endPage < totalPages && (
                      <button className="pagination-page" onClick={() => setCurrentPage(endPage + 1)}>
                        ...
                      </button>
                    )}

                    <button
                      className="pagination-button"
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, requests.totalPages))
                      }
                      disabled={currentPage === requests.totalPages}
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Create Request */}
      {addOverlayOpen && selectedProperty && (
        <CreateRequestForm
          property={{ _id: selectedProperty.value }}
          onRequestCreated={handleRequestCreated}
          onClose={() => setAddOverlayOpen(false)}
        />
      )}

      {/* Edit Request */}
      {isEditRequestOpen && editRequest && (
        <EditRequestForm
          requestId={editRequest._id}
          property={{ selectedProperty }}
          onClose={() => {
            setIsEditRequestOpen(false);
            setEditRequest(null);
          }}
          onRequestUpdated={handleRequestUpdated}
        />
      )}

      {/* Delete Confirmation */}
      {deleteOverlayOpen && (
        <DeleteConfirmation
          setIsOverlayOpen={setDeleteOverlayOpen}
          onConfirm={handleRequestDeleted}
        />
      )}
    </div>
  );
};


const fetchRequestsByProperty = async (propertyId, page = 1, limit = 10) => {
  if (!propertyId) return [];

  try {
    const allowedTypes = ['Maintenance', 'Package', 'ChangeRequest', 'Complaint', 'Custom', 'Payment', 'Service'];
    const response = await axios.get(`${config.API_URL}/v2/request/property/${propertyId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      },
      params: { page, limit, sortBy: 'createdAt', sortOrder: 'desc', type: allowedTypes.join(',') },
    });

    console.log('✅ Fetched requests for property:', propertyId, response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching requests for property:', propertyId, error);
    return { results: [], page: 1, totalPages: 1, totalResults: 0 };
  }
};

const fetchProperties = async () => {
  const response = await axios.get(`${config.API_URL}/v2/property`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
  });
  return response.data.map((item) => ({
    value: item._id,
    label: item.name,
  }));
};

export default MRequests;
